/**
 * Development-only logger utility
 *
 * **Security**: H-02 - Production Logging PII Risk
 * Prevents console.log calls from running in production to comply with GDPR/CCPA
 * data minimization requirements.
 *
 * **Usage**:
 * ```typescript
 * import { devLog } from '@core/utils/dev-logger';
 *
 * devLog('[PhoneEntry] Sending OTP to', redactedPhone);
 * devLog('[AuthService] User logged in', { uid: user.uid });
 * ```
 *
 * **Production Behavior**:
 * - All devLog() calls are no-ops (completely stripped if using tree-shaking)
 * - No PII accidentally logged to browser console or monitoring tools
 * - Complies with security-compliance.md Section "PII Policy"
 *
 * @see specs_and_project_plan/security-compliance.md
 */

import { isDevMode } from '@angular/core';

/**
 * Logs a message to the console only in development mode
 *
 * **WI-028**: Uses unknown[] for type safety while maintaining flexibility
 * 1. Production mode: Function is tree-shaken away (zero runtime cost)
 * 2. Development mode: Accepts any data type for debugging flexibility
 * 3. unknown[] provides type safety without forcing serialization
 * 4. GDPR-compliant: Never called in production
 *
 * @param args - Arguments to pass to console.log
 *
 * @example
 * ```typescript
 * devLog('[PhoneEntry] Form submitted');
 * devLog('[AuthService] OTP verified', { uid: user.uid }); // Only log non-sensitive data
 * ```
 */
export const devLog = (...args: unknown[]): void => {
  if (isDevMode()) {
    console.log(...args);
  }
};

/**
 * Logs an error to the console only in development mode
 *
 * **WI-028**: Uses unknown[] for type safety while maintaining flexibility
 * 1. Production mode: Function is tree-shaken away (zero runtime cost)
 * 2. Development mode: Accepts any data type for debugging flexibility
 * 3. unknown[] provides type safety without forcing serialization
 * 4. GDPR-compliant: Never called in production
 *
 * @param args - Arguments to pass to console.error
 *
 * @example
 * ```typescript
 * devError('[PhoneEntry] Failed to send OTP', error);
 * ```
 */
export const devError = (...args: unknown[]): void => {
  if (isDevMode()) {
    console.error(...args);
  }
};

/**
 * Logs a warning to the console only in development mode
 *
 * **WI-028**: Uses unknown[] for type safety while maintaining flexibility
 * 1. Production mode: Function is tree-shaken away (zero runtime cost)
 * 2. Development mode: Accepts any data type for debugging flexibility
 * 3. unknown[] provides type safety without forcing serialization
 * 4. GDPR-compliant: Never called in production
 *
 * @param args - Arguments to pass to console.warn
 *
 * @example
 * ```typescript
 * devWarn('[AuthService] Session expired, requiring re-login');
 * ```
 */
export const devWarn = (...args: unknown[]): void => {
  if (isDevMode()) {
    console.warn(...args);
  }
};

/**
 * Creates a collapsible group in the console only in development mode
 *
 * **WI-028**: Uses unknown[] for type safety while maintaining flexibility
 * 1. Production mode: Function is tree-shaken away (zero runtime cost)
 * 2. Development mode: Accepts any data type for debugging flexibility
 * 3. unknown[] provides type safety without forcing serialization
 * 4. GDPR-compliant: Never called in production
 *
 * @param args - Arguments to pass to console.group
 *
 * @example
 * ```typescript
 * devGroup('🚨 Error Details');
 * devError('Message:', error.message);
 * devGroupEnd();
 * ```
 */
export const devGroup = (...args: unknown[]): void => {
  if (isDevMode()) {
    console.group(...args);
  }
};

/**
 * Closes the current console group only in development mode
 *
 * **Note**: Must be paired with devGroup() call
 *
 * @example
 * ```typescript
 * devGroup('Error Context');
 * devLog('Details here');
 * devGroupEnd();
 * ```
 */
export const devGroupEnd = (): void => {
  if (isDevMode()) {
    console.groupEnd();
  }
};
